import { useProjectAccess } from '../../../../../../../../../hooks'
import { SHARE_PERMISSION } from '../../../../../../../../../permissions/project/definitions'
import type { ShareSettingsOption } from '../../../../../Share'
import { PROJECT_SHARE_TYPE } from '../types'

export const useGetShareSettingOptions = (): ShareSettingsOption[] => {
  const {
    hasAccess: hasAccessPasswordProtect,
    hasNotAvailableAccessBadge: hasAccessPasswordProtectBadge,
    badgeType: passwordProtectBadgeType,
  } = useProjectAccess(SHARE_PERMISSION.PASSWORD_PROTECT)

  const {
    hasAccess: hasAccessOnlyMembers,
    hasNotAvailableAccessBadge: hasAccessOnlyMembersBadge,
    badgeType: onlyMembersBadgeType,
  } = useProjectAccess(SHARE_PERMISSION.ONLY_PROJECT_MEMBERS)

  return [
    {
      label: 'Anyone with the link',
      value: PROJECT_SHARE_TYPE.ANYONE,
      visible: true,
      icon: (
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M7.99961 0.6C12.0754 0.6 15.3996 3.92422 15.3996 8C15.3996 12.1047 12.0754 15.4 7.99961 15.4C3.89492 15.4 0.599609 12.1047 0.599609 8C0.599609 3.92422 3.89492 0.6 7.99961 0.6ZM7.99961 14.0125C8.20195 14.0125 8.75117 13.8102 9.3582 12.625C9.61836 12.1047 9.8207 11.4977 9.99414 10.775H5.97617C6.14961 11.4977 6.35195 12.1047 6.61211 12.625C7.21914 13.8102 7.76836 14.0125 7.99961 14.0125ZM5.74492 9.3875H10.2254C10.2832 8.95391 10.3121 8.49141 10.3121 8C10.3121 7.5375 10.2832 7.075 10.2254 6.6125H5.74492C5.68711 7.075 5.68711 7.5375 5.68711 8C5.68711 8.49141 5.68711 8.95391 5.74492 9.3875ZM9.99414 5.225C9.8207 4.53125 9.61836 3.92422 9.3582 3.40391C8.75117 2.21875 8.20195 1.9875 7.99961 1.9875C7.76836 1.9875 7.21914 2.21875 6.61211 3.40391C6.35195 3.92422 6.14961 4.53125 5.97617 5.225H9.99414ZM11.6129 6.6125C11.6707 7.075 11.6996 7.5375 11.6996 8C11.6996 8.49141 11.6707 8.95391 11.6129 9.3875H13.8387C13.9543 8.95391 14.0121 8.49141 14.0121 8C14.0121 7.5375 13.9543 7.075 13.8387 6.6125H11.6129ZM10.4855 2.53672C10.8902 3.28828 11.2082 4.21328 11.4105 5.225H13.3184C12.7113 4.03984 11.6996 3.08594 10.4855 2.53672ZM5.48477 2.53672C4.2707 3.08594 3.25898 4.03984 2.65195 5.225H4.55977C4.76211 4.21328 5.08008 3.28828 5.48477 2.53672ZM1.98711 8C1.98711 8.49141 2.01602 8.95391 2.13164 9.3875H4.35742C4.29961 8.95391 4.29961 8.49141 4.29961 8C4.29961 7.5375 4.29961 7.075 4.35742 6.6125H2.13164C2.01602 7.075 1.98711 7.5375 1.98711 8ZM13.3184 10.775H11.4105C11.2082 11.8156 10.8902 12.7406 10.4855 13.4922C11.6996 12.943 12.7113 11.9891 13.3184 10.775ZM4.55977 10.775H2.65195C3.25898 11.9891 4.2707 12.943 5.48477 13.4922C5.08008 12.7406 4.76211 11.8156 4.55977 10.775Z"
            fill="currentColor"
          />
        </svg>
      ),
    },
    {
      label: 'Only project members',
      value: PROJECT_SHARE_TYPE.ONLY_MEMBERS,
      visible: hasAccessOnlyMembers || hasAccessOnlyMembersBadge,
      disabled: hasAccessOnlyMembersBadge,
      badgeType: onlyMembersBadgeType,
      icon: (
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M5.6 8C3.825 8 2.4 6.575 2.4 4.8C2.4 3.05 3.825 1.6 5.6 1.6C7.35 1.6 8.8 3.05 8.8 4.8C8.8 6.575 7.35 8 5.6 8ZM5.6 2.8C4.475 2.8 3.6 3.7 3.6 4.8C3.6 5.925 4.475 6.8 5.6 6.8C6.7 6.8 7.6 5.925 7.6 4.8C7.6 3.7 6.7 2.8 5.6 2.8ZM6.85 9.2C9.25 9.2 11.2 11.15 11.2 13.55C11.2 14.025 10.8 14.4 10.325 14.4H0.85C0.375 14.4 0 14.025 0 13.55C0 11.15 1.925 9.2 4.325 9.2H6.85ZM1.2 13.2H9.975C9.775 11.65 8.475 10.4 6.85 10.4H4.325C2.7 10.4 1.375 11.65 1.2 13.2ZM11.975 9.6C14.2 9.6 16 11.4 16 13.6C16 14.05 15.625 14.4 15.2 14.4H11.75C11.9 14.15 12 13.875 12 13.55C12 11.975 11.275 10.55 10.125 9.6H11.975ZM10.8 8C10 8 9.3 7.7 8.8 7.175C9.3 6.525 9.6 5.7 9.6 4.8C9.6 4.15 9.4 3.525 9.125 2.975C9.575 2.625 10.15 2.4 10.8 2.4C12.325 2.4 13.6 3.675 13.6 5.2C13.6 6.75 12.325 8 10.8 8Z"
            fill="currentColor"
          />
        </svg>
      ),
    },
    {
      label: 'Password protected',
      value: PROJECT_SHARE_TYPE.PASSWORD,
      visible: hasAccessPasswordProtect || hasAccessPasswordProtectBadge,
      disabled: hasAccessPasswordProtectBadge,
      badgeType: passwordProtectBadgeType,
      icon: (
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M4.375 6.1875V4.375C4.375 2.39258 5.98926 0.75 8 0.75C9.98242 0.75 11.625 2.39258 11.625 4.375V6.1875H12.5312C13.5225 6.1875 14.3438 7.00879 14.3438 8V13.4375C14.3438 14.457 13.5225 15.25 12.5312 15.25H3.46875C2.44922 15.25 1.65625 14.457 1.65625 13.4375V8C1.65625 7.00879 2.44922 6.1875 3.46875 6.1875H4.375ZM5.73438 6.1875H10.2656V4.375C10.2656 3.12891 9.24609 2.10938 8 2.10938C6.72559 2.10938 5.73438 3.12891 5.73438 4.375V6.1875ZM3.01562 13.4375C3.01562 13.6924 3.21387 13.8906 3.46875 13.8906H12.5312C12.7578 13.8906 12.9844 13.6924 12.9844 13.4375V8C12.9844 7.77344 12.7578 7.54688 12.5312 7.54688H3.46875C3.21387 7.54688 3.01562 7.77344 3.01562 8V13.4375Z"
            fill="currentColor"
          />
        </svg>
      ),
    },
  ]
}
